import {Dispatch, FormEvent, ReactNode, SetStateAction, useState} from 'react';
import backImage from '@legit-health/svg-images/images/scalp-perspectives/back.svg';
import leftImage from '@legit-health/svg-images/images/scalp-perspectives/left.svg';
import rightImage from '@legit-health/svg-images/images/scalp-perspectives/right.svg';
import topImage from '@legit-health/svg-images/images/scalp-perspectives/top.svg';
import {ApiError, useBackendClient} from '@legit.health/backend-client';
import {
  Condition,
  ConditionId,
  FILE_MAX_SIZE,
  generatePath,
  generateRandomId,
  ImageWithCrop,
  UserId
} from '@legit.health/ui';
import SendIcon from '@mui/icons-material/Send';
import {Typography, Grid, Button, Box, Alert} from '@mui/material';
import {useMutation} from '@tanstack/react-query';
import {TFunction} from 'i18next';
import ImageField from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/FollowUpForm/components/ImageField';
import MutationStatusIndicator from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/FollowUpForm/components/MutationStatusIndicator';
import BottomBar from '@/shared/components/diagnosticReport/DiagnosticReportForm/steps/BottomBar';
import {AnamnesisQuestionsValue} from '@/shared/components/diagnosticReport/DiagnosticReportForm/types';
import {processAnamnesisQuestions} from '@/shared/components/diagnosticReport/DiagnosticReportForm/utils/mapFormValuesToApiFormat';
import {EncounterId} from '@/shared/interfaces/Encounter';
import FormValues from './interfaces/FormValues';
import mapFormValuesToApiFormat from './utils/mapFormValuesToApiFormat';

type FieldErrors = Record<
  'headLeftImage' | 'headRightImage' | 'headBackImage' | 'headTopImage' | 'general',
  string | null
>;

export default function AlopeciaForm({
  patientId,
  condition,
  setCurrentStep,
  t,
  steps,
  onSuccess,
  anamnesisAnswers,
  visitIdentifier
}: {
  patientId: UserId;
  condition: Condition;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  t: TFunction;
  steps: {label: string; icon: ReactNode}[];
  onSuccess: (encounterId: EncounterId) => void;
  anamnesisAnswers: AnamnesisQuestionsValue;
  visitIdentifier: string;
}) {
  const sizeErrorMessage = t('ui:dropzone.error.fileSize', {maxSize: FILE_MAX_SIZE});
  const generalErrorMessage = t('ui:dropzone.error.general');
  const typeErrorMessage = t('ui:dropzone.error.type');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
    headLeftImage: null,
    headRightImage: null,
    headBackImage: null,
    headTopImage: null,
    general: null
  });

  const [form, setForm] = useState<FormValues>({
    headLeftImage: {
      crop: null,
      file: null,
      id: generateRandomId()
    },
    headRightImage: {
      crop: null,
      file: null,
      id: generateRandomId()
    },
    headBackImage: {
      crop: null,
      file: null,
      id: generateRandomId()
    },
    headTopImage: {
      crop: null,
      file: null,
      id: generateRandomId()
    }
  });
  const backendClient = useBackendClient();
  const {mutate, status: mutationStatus} = useMutation({
    mutationFn: async function (values: FormValues) {
      const mappedValues = await mapFormValuesToApiFormat(values);
      return await backendClient.sendRequest<{
        encounterId: EncounterId;
        conditionId: ConditionId;
      }>({
        method: 'POST',
        path: generatePath('/alopecia'),
        body: {
          ...mappedValues,
          patient: patientId,
          pathology: condition.pathology.id,
          anamnesisQuestions: processAnamnesisQuestions(anamnesisAnswers),
          visitIdentifier
        }
      });
    }
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutate(form, {
      onSuccess: function (response) {
        onSuccess(response.data.encounterId);
      },
      onError: function (error) {
        if (error instanceof ApiError) {
          if (error.content.data?.errors?.children) {
            const errors = error.content.data.errors.children;
            const newErrors = [
              'headLeftImage',
              'headRightImage',
              'headBackImage',
              'headTopImage'
            ].reduce(
              (acc, key) => ({
                ...acc,
                [key]: errors[key]?.errors?.[0] ?? null
              }),
              {general: null} as FieldErrors
            );
            setFieldErrors(newErrors);
          } else {
            setFieldErrors({
              headLeftImage: null,
              headRightImage: null,
              headBackImage: null,
              headTopImage: null,
              general: t('ui:diagnosticReport:serverError')
            });
          }
        }
      }
    });
  }

  function handleChange(
    name: 'headLeftImage' | 'headRightImage' | 'headBackImage' | 'headTopImage',
    imageWithCrop: ImageWithCrop | null
  ) {
    if (imageWithCrop) {
      setForm((v) => ({
        ...v,
        [name]: {
          id: v[name].id,
          ...imageWithCrop
        }
      }));
    } else {
      setForm((v) => ({
        ...v,
        [name]: {
          id: v[name].id,
          file: null,
          crop: null
        }
      }));
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Typography
        sx={{
          textAlign: 'center',
          mt: 4,
          mb: {xs: 0, md: 2}
        }}
      >
        {t('ui:alopeciaAreata.instructions')}
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{
          mb: 2
        }}
      >
        {(['headLeftImage', 'headRightImage', 'headTopImage', 'headBackImage'] as const).map(
          (field, index) => (
            <Grid
              key={field}
              size={{
                xs: 12,
                lg: 3
              }}
            >
              <ImageField
                value={form[field]}
                placeholderImage={[leftImage, rightImage, topImage, backImage][index] as string}
                label={t(`ui:alopeciaAreata.bodySites.${field.replace('Image', '')}`)}
                name={field}
                sizeErrorMessage={sizeErrorMessage}
                generalErrorMessage={generalErrorMessage}
                typeErrorMessage={typeErrorMessage}
                onChange={(imageWithCrop) => handleChange(field, imageWithCrop)}
                dataTestId={field}
              />
              {fieldErrors[field] && (
                <MutationStatusIndicator
                  mutationStatus={mutationStatus as 'error' | 'idle' | 'success' | 'loading'}
                  error={fieldErrors[field]}
                  t={t}
                />
              )}
            </Grid>
          )
        )}
      </Grid>
      {fieldErrors.general && (
        <Box
          sx={{
            mt: 4,
            width: {
              xs: '100%',
              lg: '50%'
            },
            mx: 'auto'
          }}
        >
          <Alert severity="error">{fieldErrors.general}</Alert>
        </Box>
      )}
      <BottomBar
        activeStep={2}
        isBackEnabled
        onBack={() => setCurrentStep((s) => s - 1)}
        t={t}
        totalSteps={steps.length}
        rightButton={
          <Button
            color="secondary"
            onClick={undefined}
            disableElevation
            disabled={
              !(
                form.headBackImage.file ||
                form.headLeftImage.file ||
                form.headRightImage.file ||
                form.headTopImage.file
              ) || mutationStatus === 'pending'
            }
            size="medium"
            variant="contained"
            endIcon={<SendIcon />}
            type="submit"
            data-testid="stepperNextButton"
          >
            {mutationStatus === 'pending' ? t('ui:sending') : t('ui:send')}
          </Button>
        }
      />
    </form>
  );
}
